import { useMemo } from "react";
import { CalendarCheck } from "lucide-react";
import { useTranslation } from "@ferrivia/i18n";
import { Text } from "@ferrivia/ui";
import { useJourneyCopy } from "@ferrivia/journey-ui";
import type { PatientAction, PatientActionCheckIn, TimelineEntry } from "@ferrivia/api-contract";
import { useCustomerJourney } from "../../../../hooks/useCustomerJourney";
import { KIND_ICON, useActionLabels } from "../../../../actions/kinds";
import { buildCareLog, type ComingUpItem, type HistoryItem } from "../../../../actions/careLog";
import { toIsoDate } from "../../../../actions/plan";
import { useEntryContent } from "../../../../components/TimelineFeed/entryContent";
import type { FeedEntry } from "../../../../components/TimelineFeed/types";
import s from "./JourneyPane.module.css";

export interface JourneyPaneProps {
  customerId: string;
  actions: PatientAction[];
  checkIns: PatientActionCheckIn[];
  timelineEntries: TimelineEntry[];
  onOpenLabs: (entryKind: FeedEntry["kind"]) => void;
  onOpenAction?: (actionId: string) => void;
}

function ComingUpRow({ item }: { item: ComingUpItem }) {
  const { t } = useTranslation("clinic");
  const { formatDay, formatTimestampDay, channelLabel, titleLabel } = useActionLabels();
  const isLab = item.action.kind === "lab";
  const Icon = isLab ? KIND_ICON.lab : CalendarCheck;

  return (
    <li className={`${s.entry} ${s.upcoming}`}>
      <span className={s.mark} aria-hidden="true">
        <Icon size={14} strokeWidth={1.75} />
      </span>
      <div className={s.body}>
        <div className={s.top}>
          <Text as="span" className={s.label}>
            {isLab
              ? item.action.title
              : t("journeyPane.checkInVia", {
                  channel: item.channel ? channelLabel(item.channel) : "",
                  defaultValue: "Check-in · {{channel}}",
                })}
          </Text>
          <Text as="span" size="xs" className={s.when}>
            {isLab
              ? t("journeyPane.drawBefore", { date: formatDay(item.on), defaultValue: "before {{date}}" })
              : formatDay(item.on)}
          </Text>
        </div>
        <Text as="span" size="xs" className={s.sub}>
          {t("journeyPane.setBy", {
            name: `${item.setBy.name}, ${titleLabel(item.setBy.title)}`,
            date: formatTimestampDay(item.setBy.at),
            defaultValue: "Set by {{name}} on {{date}}",
          })}
        </Text>
      </div>
    </li>
  );
}

interface HistoryRowProps {
  item: HistoryItem;
  onOpenLabs: JourneyPaneProps["onOpenLabs"];
  onOpenAction?: (actionId: string) => void;
}

function HistoryRow({ item, onOpenLabs, onOpenAction }: HistoryRowProps) {
  const { t } = useTranslation("clinic");
  const { formatTimestampDay } = useActionLabels();
  const entryContent = useEntryContent();

  const feedEntry: FeedEntry =
    item.kind === "action"
      ? { id: item.id, kind: "action", occurredAt: item.occurredAt, action: item.action, previous: item.previous }
      : item.kind === "action_check_in"
        ? { id: item.id, kind: "action_check_in", occurredAt: item.occurredAt, checkIn: item.checkIn, action: item.action }
        : item.entry;
  const content = entryContent(feedEntry as Exclude<FeedEntry, { kind: "message" }>);
  const replaced = item.kind === "action" && !item.active;

  return (
    <li className={s.entry}>
      <span className={s.mark} aria-hidden="true">
        <content.Icon size={14} strokeWidth={1.75} />
      </span>
      <div className={s.body}>
        <div className={s.top}>
          <Text as="span" className={`${s.label} ${replaced ? s.struck : ""}`}>
            {content.title}
          </Text>
          {item.kind === "action" && item.active && (
            <span className={s.activePill}>{t("journeyPane.active", "Active")}</span>
          )}
        </div>
        {content.lines.map((line) => (
          <Text key={line} as="span" size="xs" className={s.sub}>
            {line}
          </Text>
        ))}
        {content.supersede && (
          <Text as="span" size="xs" className={s.sub}>
            {content.supersede}
          </Text>
        )}
        <div className={s.foot}>
          <Text as="span" size="xs" className={s.when}>
            {formatTimestampDay(item.occurredAt)}
          </Text>
          {item.kind === "action" && item.replacedAt && (
            <Text as="span" size="xs" className={s.when}>
              {t("journeyPane.replacedOn", {
                date: formatTimestampDay(item.replacedAt),
                defaultValue: "replaced {{date}}",
              })}
            </Text>
          )}
          {content.opensLabs && (
            <button type="button" className={s.link} onClick={() => onOpenLabs(feedEntry.kind)}>
              {t("timelineFeed.openInLabs", "Open in Labs")}
            </button>
          )}
          {content.opensAction && onOpenAction && (
            <button type="button" className={s.link} onClick={() => onOpenAction(content.opensAction!)}>
              {t("timelineFeed.openInPlan", "Open in plan")}
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/* The clinic console's Journey tab: a care log.
 *
 * A one-line programme stage, then what staff have scheduled ("Coming up"),
 * a Today rule, then everything that has happened, newest first. Direction JA
 * of product/prototypes/clinic-actions.html.
 *
 * Every dated line is either a date a named staff member typed or the moment
 * a record was written. There is no elapsed time, no overdue state and no
 * value from a lab result: a lab entry is provenance and a count, with "Open
 * in Labs" for the numbers. The stage is computed from counts and dates only
 * (apps/api's journey-purity.test.ts), which is why a staff surface may show a
 * position when the Labs pane may not show a verdict.
 *
 * No tracker call here: clinic.patient_journey_viewed is emitted by the route,
 * and the action records arrive from the workspace's one patient_actions read.
 */
export function JourneyPane({ customerId, actions, checkIns, timelineEntries, onOpenLabs, onOpenAction }: JourneyPaneProps) {
  const { t } = useTranslation("clinic");
  const { stepFor } = useJourneyCopy();
  const { formatDay, formatTimestampDay } = useActionLabels();
  const state = useCustomerJourney(customerId);

  const today = toIsoDate(new Date());
  const log = useMemo(
    () => buildCareLog({ actions, checkIns, timelineEntries, today }),
    [actions, checkIns, timelineEntries, today],
  );

  if (state.status === "loading") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("journeyPane.loading", "Loading journey…")}
      </Text>
    );
  }

  if (state.status === "error") {
    return (
      <Text as="p" size="sm" className={s.helper}>
        {t("journeyPane.error", "Couldn't load the journey.")}
      </Text>
    );
  }

  const current = state.journey.rungs.find((rung) => rung.state === "current");

  return (
    <div className={s.root}>
      {current && (
        <Text as="p" size="sm" className={s.stage}>
          {t("journeyPane.stage", "Programme stage:")} <strong>{stepFor(current)}</strong>
          {current.reachedOn &&
            ` · ${t("journeyPane.stageSince", {
              date: formatDay(current.reachedOn),
              defaultValue: "since {{date}}",
            })}`}
        </Text>
      )}

      {log.comingUp.length > 0 && (
        <section className={s.section} aria-labelledby="journey-coming-up">
          <Text as="h2" id="journey-coming-up" size="sm" weight="bold" className={s.heading}>
            {t("journeyPane.comingUp", "Coming up")}
          </Text>
          <ul className={s.list}>
            {log.comingUp.map((item) => (
              <ComingUpRow key={item.action.id} item={item} />
            ))}
          </ul>
        </section>
      )}

      <div className={s.todayRule} role="separator">
        <span>{t("journeyPane.today", { date: formatTimestampDay(new Date().toISOString()), defaultValue: "Today · {{date}}" })}</span>
      </div>

      <section className={s.section} aria-labelledby="journey-history">
        <Text as="h2" id="journey-history" size="sm" weight="bold" className={s.heading}>
          {t("journeyPane.history", "History")}
        </Text>
        {log.history.length === 0 ? (
          <Text as="p" size="sm" className={s.helper}>
            {t("journeyPane.historyEmpty", "Nothing recorded yet.")}
          </Text>
        ) : (
          <ul className={s.list}>
            {log.history.map((item) => (
              <HistoryRow key={item.id} item={item} onOpenLabs={onOpenLabs} onOpenAction={onOpenAction} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
